import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import type { Quadrant as QuadrantId } from '@cleartask/shared';
import { DetalheNaUrl, useDetalheNaUrl } from '../tasks/DetalheNaUrl.js';
import {
  newTaskId,
  useCompleteTask,
  useCreateTask,
  useReopenTask,
} from '../tasks/useTasks.js';
import { Quadrant, QUADRANT_DROPPABLE_PREFIX } from './Quadrant.js';
import {
  QUADRANT_ORDER,
  useMatrixTasks,
  usePinQuadrant,
  useResetQuadrant,
} from './useQuadrant.js';

/** Converte o id do droppable de volta no quadrante que ele representa. */
function quadrantFromDroppableId(id: string): QuadrantId | null {
  if (!id.startsWith(QUADRANT_DROPPABLE_PREFIX)) return null;

  const value = Number(id.slice(QUADRANT_DROPPABLE_PREFIX.length));
  return value === 1 || value === 2 || value === 3 || value === 4 ? value : null;
}

/**
 * Grade 2x2 da matriz (spec 9.2).
 *
 * No desktop os quatro quadrantes ocupam a viewport e o arrastar entre eles e
 * a interacao principal. No mobile viram uma coluna rolavel: uma grade 2x2 em
 * 390 px daria quadrantes de ~180 px, estreitos demais tanto para ler um
 * titulo quanto para acertar um alvo de drop.
 */
export function MatrixBoard() {
  const { byQuadrant, doneByQuadrant, now, isPending, isError, error } = useMatrixTasks();
  const { pin } = usePinQuadrant();
  const { reset } = useResetQuadrant();
  const completeTask = useCompleteTask();
  const reopenTask = useReopenTask();
  const createTask = useCreateTask();
  // Mesmo endereco por item da aba Tarefas (§8m): sem ele, clicar num card da
  // matriz abria um painel que o "voltar" do aparelho nao fechava e que nenhum
  // link alcancava. A tela nao tem filtro na URL, entao o sufixo e vazio.
  const { detalhe, abrir, fechar, abrirPorId } = useDetalheNaUrl('/matriz');

  /**
   * Cria fixada no quadrante em que a pessoa clicou.
   *
   * `quadrantAuto: false` e o mesmo par de campos que arrastar um card grava
   * (ver `usePinQuadrant`): sem desligar a derivacao, o servidor recalcularia
   * o quadrante na leitura seguinte e a tarefa sairia de onde nasceu.
   *
   * Sem prazo: a matriz classifica por urgencia e importancia, e carimbar uma
   * data que a pessoa nao digitou inventaria a metade "urgente" da decisao.
   */
  function adicionar(title: string, quadrant: QuadrantId) {
    createTask.mutate({ id: newTaskId(), title, quadrant, quadrantAuto: false });
  }

  /*
    A linha inteira arrasta (§8ab). Mouse so depois de 8 px — senao um clique
    vira micro arrasto —, e no toque so com toque longo (250 ms), como no
    TickTick: com distancia, o dedo que rola um quadrante agarraria a tarefa.
  */
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } }),
    useSensor(KeyboardSensor),
  );

  function onDragEnd(event: DragEndEvent) {
    const destino = event.over?.id;
    if (typeof destino !== 'string') return;

    const quadrant = quadrantFromDroppableId(destino);
    if (quadrant === null) return;

    const arrastada = QUADRANT_ORDER.flatMap((q) => byQuadrant[q]).find(
      (t) => t.id === event.active.id,
    );
    if (!arrastada) return;

    // Soltar no proprio quadrante nao e override. Fixar o que ja estava certo
    // desligaria a derivacao automatica sem o usuario ter pedido nada.
    if (arrastada.quadrant === quadrant) return;

    pin(arrastada, quadrant);
  }

  if (isPending) return <Skeleton />;

  if (isError) {
    return (
      <p className="rounded bg-surface p-4 text-sm text-overdue" role="alert">
        {error instanceof Error ? error.message : 'Não foi possível carregar a matriz.'}
      </p>
    );
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      {/*
        2x2 em qualquer largura, como o TickTick tambem no celular: a matriz
        existe para mostrar os quatro quadrantes ao mesmo tempo. Empilhados,
        o celular via um por vez e a pagina virava uma lista comprida.
      */}
      <div className="grid min-h-0 flex-1 grid-cols-2 grid-rows-2 gap-2 md:gap-3">
        {QUADRANT_ORDER.map((quadrant) => (
          <Quadrant
            key={quadrant}
            quadrant={quadrant}
            tasks={byQuadrant[quadrant]}
            done={doneByQuadrant[quadrant]}
            now={now}
            onMove={pin}
            onReset={reset}
            onComplete={(task) => completeTask.mutate(task)}
            onReopen={(task) => reopenTask.mutate(task)}
            onOpen={abrir}
            onAdd={adicionar}
          />
        ))}
      </div>

      <DetalheNaUrl detalhe={detalhe} fechar={fechar} abrirPorId={abrirPorId} />
    </DndContext>
  );
}

function Skeleton() {
  return (
    <div
      className="grid flex-1 grid-cols-2 grid-rows-2 gap-2 md:gap-3"
      aria-hidden="true"
    >
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="animate-pulse rounded-lg bg-surface-2" />
      ))}
    </div>
  );
}
